import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import _moment from 'moment';
import { default as _rollupMoment } from 'moment';
const moment = _rollupMoment || _moment;

import { fetchUserAttributes } from 'aws-amplify/auth';

import { SharedService } from '../../layouts/shared-service';
import { AuthService } from '../../service';
import { UserAdminService } from '../../service/user-admin.service';
import { User } from '../../model/types';
import { UsageReport } from '../../model/user-admin.model';

export const MY_FORMATS = {
    parse: { dateInput: 'MM/YYYY' },
    display: {
        dateInput: 'MM/DD/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@Component({
    selector: 'app-usage-reports',
    templateUrl: './usage-reports.component.html',
    styleUrls: ['./usage-reports.component.scss'],
    providers: [
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS }
    ],
    standalone: false
})
export class UsageReportsComponent implements OnInit {
    @ViewChild('reportPaginator', { static: false }) reportPaginator: MatPaginator;
    @ViewChild('reportSort', { static: false }) reportSort: MatSort;

    pageTitle = 'Usage Reports';
    signedInUser: any;
    illegalUser: boolean = false;

    reportDataSource: MatTableDataSource<UsageReport>;
    reportColumns: string[] = ['analysisDate', 'userId', 'analysisId', 'analysisType', 'analysisStatus', 'analysisFilename', 'projectCode'];
    reports: UsageReport[] = [];
    reportsLoading: boolean = false;
    reportsError: string = '';
    reportOrganization: string = '';
    reportStartDate: string;
    reportEndDate: string;
    reportLastKey: unknown = null;

    constructor(
        private _sharedService: SharedService,
        private authService: AuthService,
        private router: Router,
        private userAdminService: UserAdminService
    ) {
        this.reportDataSource = new MatTableDataSource(this.reports);
        setTimeout(() => this._sharedService.emitChange(this.pageTitle));
    }

    ngOnInit(): void {
        this.authService.getCurrentUser((err, user: User) => {
            this.signedInUser = user;
            if (!this.signedInUser || !this.signedInUser.signedIn) {
                this.router.navigate(['/extra-layout/signin']);
                return;
            }
            this.resolveAccessAndLoad();
        });
    }

    private async resolveAccessAndLoad(): Promise<void> {
        let access = sessionStorage.getItem('access');
        try {
            const attrs = await fetchUserAttributes();
            const live = attrs?.['custom:access'];
            if (live) {
                access = live;
                sessionStorage.setItem('access', live);
            }
        } catch {
            // Fall back to whatever sessionStorage holds.
        }
        if (access === 'SU') {
            this.illegalUser = false;
            this.loadReports();
        } else {
            this.illegalUser = true;
        }
    }

    // --- Usage Reports ---

    loadReports(append: boolean = false): void {
        this.reportsLoading = true;
        this.reportsError = '';
        this.userAdminService.getReports({
            organization: this.reportOrganization || undefined,
            start_date: this.reportStartDate || undefined,
            end_date: this.reportEndDate || undefined,
            last_evaluated_key: append ? this.reportLastKey : undefined
        }).subscribe({
            next: (response) => {
                const items = response.Items || [];
                this.reports = append ? [...this.reports, ...items] : items;
                this.reportLastKey = response.LastEvaluatedKey || null;
                this.reportDataSource = new MatTableDataSource(this.reports);
                setTimeout(() => this.reportDataSource.paginator = this.reportPaginator);
                setTimeout(() => this.reportDataSource.sort = this.reportSort);
                this.reportsLoading = false;
            },
            error: (err) => {
                this.reportsLoading = false;
                this.reportsError = this.getErrorMessage(err);
            }
        });
    }

    loadMoreReports(): void {
        if (this.reportLastKey) {
            this.loadReports(true);
        }
    }

    filterReports(
        dateRangeStart: HTMLInputElement,
        dateRangeEnd: HTMLInputElement,
        organizationInput: HTMLInputElement
    ): void {
        this.reportStartDate = dateRangeStart.value;
        this.reportEndDate = dateRangeEnd.value;
        this.reportOrganization = organizationInput.value.trim();
        this.reportLastKey = null;
        this.loadReports(false);
    }

    downloadReport(): void {
        if (!this.reports.length) {
            return;
        }
        const headers = ['Analysis Date', 'User ID', 'Analysis ID', 'Analysis Type', 'Analysis Status', 'Input Filename', 'Project Code'];
        const rows = this.reports.map(r => [
            r.AnalysisDate,
            r.UserId,
            r.AnalysisId,
            r.AnalysisType,
            r.AnalysisStatus,
            r.AnalysisFilename,
            r.ProjectCode
        ]);
        const csv = [headers, ...rows]
            .map(row => row.map(cell => this.escapeCsv(cell)).join(','))
            .join('\r\n');
        const dateStr = new Date().toISOString().split('T')[0];
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `usage-reports-${dateStr}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }

    // --- Helpers ---

    private escapeCsv(value: string): string {
        const str = value == null ? '' : String(value);
        if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
            return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
    }

    private getErrorMessage(err: { status?: number }): string {
        switch (err && err.status) {
            case 400: return 'Bad Request. Please verify the input values.';
            case 401: return 'Unauthorized. Please login again.';
            case 403: return 'Access denied. You do not have permission to perform this action.';
            case 404: return 'Resource not found.';
            case 500: return 'Server error. Please try again later.';
            case 502: return 'Bad Gateway. The server is temporarily unavailable.';
            case 503: return 'Service Unavailable. Please try again later.';
            case 504: return 'Gateway Timeout. Server took too long to respond.';
            default:  return 'Unexpected error occurred. Please try again.';
        }
    }
}
